import { Component, OnInit, OnDestroy, ViewChild, ElementRef, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { CommandChannelResponse, ZoomService, NetworkQualityState } from '../services/zoom.service';
import { ChangeDetectorRef } from '@angular/core';
import { VideoQuality, ParticipantPropertiesPayload, Participant } from '@zoom/videosdk';
import { ButtonModule } from 'primeng/button';
import { ZoomClientType } from '../services/zoom.service';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService, ToastMessageOptions } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AuthService } from '../services/auth.service';
import { CommandEvent, CommandEventType } from '../services/zoom.service';
import { SessionService } from '../services/session.service';
import { SessionManagerService } from '../services/session-manager.service';
import { NotificationService } from '../services/notification.service';
import { MediaGalleryComponent, MediaItem } from '../components/media-gallery/media-gallery.component';
import { MediaThumbnailDrawerComponent } from '../components/media-thumbnail-drawer/media-thumbnail-drawer.component';
import { MediaService } from '../services/media.service';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { firstValueFrom } from 'rxjs';
import { ChatComponent } from '../components/chat/chat.component';
import { ChatService } from '../services/chat.service';
import { GeolocationService } from '../services/geolocation.service';
import { GuestLinkDialogComponent, GuestLinkSession } from '../components/guest-link-dialog/guest-link-dialog.component';
import { NetworkQualityIndicatorComponent, NetworkQuality, QualityTrend } from '../components/network-quality-indicator/network-quality-indicator.component';
import { LocationMapDialogComponent } from '../components/location-map-dialog/location-map-dialog.component';

@Component({
    selector: 'app-coordinator',
    templateUrl: './coordinator.component.html',
    styleUrls: ['./coordinator.component.scss'],
    imports: [CommonModule, FormsModule, ButtonModule, SelectModule, TooltipModule, ConfirmDialogModule, MediaGalleryComponent, MediaThumbnailDrawerComponent, DialogModule, ChatComponent, GuestLinkDialogComponent, NetworkQualityIndicatorComponent, LocationMapDialogComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CoordinatorComponent implements OnInit, OnDestroy {
  @ViewChild('inspectorVideoContainer', { static: true }) inspectorVideoContainer!: ElementRef<HTMLElement>;
  @ViewChild('coordinatorVideo', { static: true }) coordinatorVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild(ChatComponent) chatComponent?: ChatComponent;
  private inspectorVideoElement: HTMLElement | null = null;
  sessionId: string = "";
  zoomClient: ZoomClientType | void | null = null;
  videoQuality = VideoQuality.Video_1080P;
  sessionEnded = false;
  cloudRecording: any = null;
  isRecording = false;
  recordingSequence = 0; // Track which recording segment this is (0, 1, 2, ...)
  recordingStartTime: string | null = null; // ISO timestamp when recording started
  isInspectorOnMobileDevice = false; // Track if inspector is on mobile device (needs recording stop for photos)
  wasRecordingBeforePhotoCapture = false; // Track if we were recording when we stopped for a photo capture
  micDevices: any[] = [];
  speakerDevices: any[] = [];
  inspectorCameraList: any[] | null = null;
  selectedMic: any;
  selectedSpeaker: any;
  isSessionStarted = false;
  isInspectorConnected = false;
  isMediaNetworkConnected = false;
  isSessionStarting = false;
  selectedInspectorCamera: any;
  sessionMedia$: Observable<MediaItem[]> = new Observable();
  sessionPhotos$: Observable<MediaItem[]> = new Observable();
  imageCount = 0;
  
  // UI State Management
  showCoordinatorVideo = true; // Default to show when session starts
  showThumbnailDrawer = false; // Default to hide thumbnail drawer
  isThumbnailDrawerPinned = false; // Track if thumbnail drawer is pinned
  showMediaGalleryDialog = false; // Full media gallery dialog
  isFullscreen = false;
  showDeviceSettings = false; // Show device selection overlay
  isCapturingPhoto = false; // Track photo capture progress
  private photoCaptureTimeout: any = null; // Safety timeout for capture guard
  coordinatorVideoActive = false; // Track if coordinator video stream is actually running
  usingCanvasForCoordinator = false; // Track if we're using canvas instead of video element
  showChat = false; // Chat hidden by default
  
  // Guest Link Dialog
  showGuestLinkDialog = false;
  guestLinkSession: GuestLinkSession | null = null;

  // Location Tracking
  locationTrackingActive = false;
  showLocationDialog = false;
  currentLocation: { latitude: number; longitude: number } | null = null;
  locationMapImageUrl: string | null = null;
  locationMapGoogleUrl: string | null = null;
  mapImageUploaded = false;

  // Network Quality
  networkQuality: NetworkQuality = NetworkQuality.GOOD;
  networkTrend: QualityTrend = QualityTrend.STABLE;
  networkLatency?: number;
  private networkQualitySubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private zoomService: ZoomService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private authService: AuthService,
    private sessionService: SessionService,
    private sessionManagerService: SessionManagerService,
    private notify: NotificationService,
    private mediaService: MediaService,
    public chatService: ChatService,
    private geolocationService: GeolocationService
  ) {}

  test() {
    this.getCameraList();
  }
  


  async ngOnInit(): Promise<void> {
    this.sessionId = this.route.snapshot.paramMap.get('sessionId') || '';
    this.sessionMedia$ = this.mediaService.media$;
    
    // Create filtered observable for photos only (for thumbnail drawer)
    this.sessionPhotos$ = this.sessionMedia$.pipe(
      map(media => media.filter(item => this.isImage(item)))
    );
    
    // Load existing media for this session immediately
    if (this.sessionId) {
      await this.loadSessionMedia();
    }
    
    this.notify.showToast('info', 'GoVideo', 'Connecting to media network...');
    
    // Add window resize listener
    window.addEventListener('resize', this.handleWindowResize);
    
    if (this.sessionId) {
      const authorized = this.authService.authorize(this.sessionId);
      if (authorized && typeof authorized.role === 'number' && authorized.userIdentity && authorized.sessionId) {
        this.zoomClient = await this.zoomService.connect(authorized.sessionId, authorized.userIdentity, authorized.role);
        if (!this.zoomClient) {
          this.notify.showToast('error', 'GoVideo', 'Failed to connect to media network.', 0);
          this.router.navigate(['/contact-us']);
        } else {
          this.isMediaNetworkConnected = true;
          this.setupEventListeners();

          // Subscribe to network quality updates
          this.networkQualitySubscription = this.zoomService.networkQuality$.subscribe(
            (state: NetworkQualityState) => {
              this.networkQuality = state.quality;
              this.networkTrend = state.trend;
              this.networkLatency = state.latency;
              this.cdr.detectChanges();
            }
          );

          // Initialize chat service
          try {
            await this.chatService.initializeChat();
            console.log('Chat service initialized for coordinator');
          } catch (error) {
            console.error('Error initializing chat service:', error);
          }

          this.notify.showToast('success', 'GoVideo', 'Connected to media network.');
        }
      } else {
        this.notify.showToast('error', 'GoVideo', 'Not authorized to access this session.', 0);
        this.router.navigate(['/contact-us']);
      }
    } else {
      this.router.navigate(['/contact-us']);
    }
  }

  ngOnDestroy(): void {
    this.stopMediaRefresh();

    // Clean up photo capture safety timeout
    if (this.photoCaptureTimeout) {
      clearTimeout(this.photoCaptureTimeout);
      this.photoCaptureTimeout = null;
    }

    // Remove window resize listener
    window.removeEventListener('resize', this.handleWindowResize);

    // Clean up inspector video
    this.detachInspectorVideo();

    // Clean up chat service
    this.chatService.cleanup();

    // Unsubscribe from network quality updates
    if (this.networkQualitySubscription) {
      this.networkQualitySubscription.unsubscribe();
    }

    if (!this.sessionEnded) {
      this.zoomService.leave();
      this.zoomService.destroyClient();
    }
  }
  
  private detachInspectorVideo(): void {
    try {
      if (this.inspectorVideoElement && this.zoomClient) {
        const stream = this.zoomClient.getMediaStream();
        const inspector = this.zoomService.inspector;
        
        if (stream && inspector) {
          // Detach the video using the Zoom SDK
          stream.detachVideo(inspector.userId);
        }
        
        // Remove the video element from DOM
        if (this.inspectorVideoElement.parentNode) {
          this.inspectorVideoElement.parentNode.removeChild(this.inspectorVideoElement);
        }
        
        this.inspectorVideoElement = null;
      }
      
      this.isInspectorConnected = false;
    } catch (error) {
      console.error('Error detaching inspector video:', error);
    }
  }
  
  private handleWindowResize = () => {
    setTimeout(() => this.resizeInspectorCanvas(), 100);
  };

  // async initZoom() {
  //   const userName = 'Coordinator';
  //   this.cdr.detach();
  //   this.zoomClient = await this.zoomService.connect(this.sessionId, userName, 1);
  //   if (!this.zoomClient) {
  //     throw new Error('Failed to initialize Zoom client');
  //   }
  //   this.setupEventListeners();
  //   this.startInspectorVideo();      
  //   this.cdr.reattach();
  //   this.cdr.detectChanges();
  // }

  setupEventListeners() {
    if (!this.zoomClient) {
      throw new Error('Zoom client not initialized');
    }
    this.zoomService.on('peer-video-state-change', (payload: { action: "Start" | "Stop"; userId: number }) => {
      const participant = this.zoomService?.client?.getUser(payload.userId);
      
      if (payload.action === 'Start') {
        if (participant?.userIdentity === "inspector") {
          this.cdr.detach();
          this.startInspectorVideo(participant);
          this.cdr.reattach();
          this.cdr.detectChanges();
          
          // If we stopped recording for photo capture (mobile devices), restart it now
          if (this.isInspectorOnMobileDevice && !this.isRecording && this.wasRecordingBeforePhotoCapture && this.cloudRecording) {
            console.log('✅ Inspector video restarted (mobile device), auto-restarting recording...');
            setTimeout(async () => {
              try {
                console.log('🔴 Restarting recording after photo capture...');
                await this.cloudRecording.startCloudRecording();
                this.isRecording = true;
                this.recordingStartTime = new Date().toISOString();
                this.wasRecordingBeforePhotoCapture = false; // Reset flag after restarting
                console.log('✅ Recording restarted. Sequence:', this.recordingSequence);
                this.notify.showToast('success', 'Recording', 'Recording resumed');
              } catch (error) {
                console.error('❌ Error restarting recording:', error);
                this.notify.showToast('error', 'Error', 'Failed to restart recording');
                this.wasRecordingBeforePhotoCapture = false; // Reset flag even on error
              }
            }, 500); // Small delay to ensure video stream is stable
          } else if (this.isInspectorOnMobileDevice && this.wasRecordingBeforePhotoCapture) {
            // Reset flag if video restarted but recording wasn't restarted for some reason
            console.log('⚠️ Video restarted but recording not auto-restarted, resetting flag');
            this.wasRecordingBeforePhotoCapture = false;
          }
        }
      } else if (payload.action === 'Stop') {
        if (participant?.userIdentity === "inspector") {
          this.detachInspectorVideo();
          this.notify.showToast('info', 'GoVideo', 'Inspector has stopped the video');
          this.cdr.detectChanges();
        }
      }
    });

    this.zoomService.on('user-added', (payload: ParticipantPropertiesPayload[]) => {
      if (payload.length && payload[0].userIdentity === 'inspector') {
        this.isInspectorConnected = true;
        this.notify.showToast('info', 'GoVideo', 'INSPECTOR has joined the session');
        
        // Try to start inspector video after a short delay
        setTimeout(() => {
          this.startInspectorVideo();
        }, 1000);
      }
    });

    this.zoomService.on('user-removed', (payload: ParticipantPropertiesPayload[]) => {
      if (payload.length && payload[0].userIdentity === 'inspector') {
        this.detachInspectorVideo();
        this.notify.showToast('info', 'GoVideo', 'INSPECTOR has left the session');
        this.cdr.detectChanges();
      }
    });

    // Listen for photo capture completion and device info
    this.zoomService.on('command-channel-message', (payload: any) => {
      try {
        const event = JSON.parse(payload.text);
        console.log('📨 Received command:', event.type);
        
        // Capture device info from inspector's acknowledge message
        if (event.type === CommandEventType.IMAGE_CAPTURE_ACKNOWLEDGE && event.data?.isMobileDevice !== undefined) {
          this.isInspectorOnMobileDevice = event.data.isMobileDevice;
          console.log('📱 Inspector device type:', this.isInspectorOnMobileDevice ? 'Mobile (requires recording stop)' : 'Desktop (recording continues)');
        }

        // Reset capture guard when inspector confirms capture+upload is done
        if (event.type === CommandEventType.IMAGE_CAPTURE_COMPLETED) {
          console.log('✅ Photo capture completed by inspector');
          if (this.photoCaptureTimeout) {
            clearTimeout(this.photoCaptureTimeout);
            this.photoCaptureTimeout = null;
          }
          this.isCapturingPhoto = false;
          this.cdr.detectChanges();
        }
        
        // Handle location tracking started
        if (event.type === CommandEventType.LOCATION_TRACKING_STARTED) {
          this.locationTrackingActive = true;
          this.currentLocation = {
            latitude: event.data.latitude,
            longitude: event.data.longitude
          };
          this.notify.showToast('success', 'GoVideo', 'Inspector location tracking started');
          console.log('📍 Location tracking started:', this.currentLocation);
          
          // Generate and upload map image
          this.generateAndUploadMapImage();
        }
      } catch (error) {
        console.error('Error parsing command channel message:', error);
      }
    });
  }

  async generateAndUploadMapImage(): Promise<void> {
    if (!this.currentLocation || this.mapImageUploaded) {
      return;
    }

    try {
      // Get map image from API (use 'latest' mode for the uploaded map)
      this.locationMapImageUrl = await this.geolocationService.getMapImage(this.sessionId, 'latest', 640, 640);

      // Get Google Maps URL
      this.locationMapGoogleUrl = await this.geolocationService.getMapUrl(this.sessionId);

      // Convert base64 to blob
      const base64Data = this.locationMapImageUrl.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });

      // Upload to S3 via MediaService
      const filename = `location-map-${this.sessionId}-${Date.now()}.jpg`;
      const mediaItem = await this.mediaService.uploadMediaFile(blob, this.sessionId, filename, 'Location Map');

      this.mapImageUploaded = true;
      this.notify.showToast('success', 'GoVideo', 'Location map added to media gallery');
      console.log('📍 Map image uploaded:', mediaItem);

      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error generating/uploading map image:', error);
      this.notify.showToast('error', 'GoVideo', 'Failed to generate location map');
    }
  }

  toggleChat(): void {
    this.showChat = !this.showChat;
    
    // Mark messages as read when showing chat
    if (this.showChat) {
      this.chatService.markAsRead();
    }
  }

  async startSession(event: any): Promise<void> {
    try {
      if (!this.zoomClient) {
        throw new Error('Zoom client not initialized');
      }
      this.isSessionStarting = true;
      const stream = this.zoomClient.getMediaStream();
      if (!stream) {
        throw new Error('Media stream not found');
      }

      const coordinatorVideo = this.coordinatorVideo.nativeElement;

      if (!coordinatorVideo) {
        throw new Error('Coordinator video element not found');
      }
      this.cdr.detach();
      
      // Set session started BEFORE starting video to ensure overlay is visible
      this.isSessionStarted = true;
      this.cdr.detectChanges(); // Force immediate UI update for Angular v20
      
      await stream.startAudio();
      
      
      // Try new SDK approach with manual stream handling
      try {
        // Try the traditional approach first
        await stream.startVideo({
          videoElement: coordinatorVideo,
        });
        this.coordinatorVideoActive = true;
        this.usingCanvasForCoordinator = false;
      } catch (error) {
        // Fallback: try manual stream assignment
        try {
          await stream.startVideo();
          const mediaStream = (stream as any).getVideoStream?.() || (stream as any).getStream?.();
          if (mediaStream && coordinatorVideo) {
            coordinatorVideo.srcObject = mediaStream;
            coordinatorVideo.play();
            this.coordinatorVideoActive = true;
            this.usingCanvasForCoordinator = false;
          } else {
            throw new Error('Could not get media stream from Zoom SDK');
          }
        } catch (altError) {
          console.error('Video startup failed, will attempt force binding:', altError);
          throw altError;
        }
      }
      
      
      // Force video binding for newer Zoom SDK / Angular v20 compatibility
      setTimeout(async () => {
        try {
          const currentUser = this.zoomClient?.getCurrentUserInfo();
          if (currentUser && coordinatorVideo && coordinatorVideo.srcObject === null) {
            // Try to stop and restart video with explicit element binding
            await stream.stopVideo();
            await new Promise(resolve => setTimeout(resolve, 100));
            
            await stream.startVideo({
              videoElement: coordinatorVideo,
            });
            
            // If still no srcObject, try renderVideo approach (fallback to canvas)
            if (coordinatorVideo.srcObject === null) {
              const canvas = document.createElement('canvas');
              canvas.width = 200;
              canvas.height = 150;
              canvas.className = 'coordinator-video';
              
              const parent = coordinatorVideo.parentElement;
              if (parent) {
                parent.replaceChild(canvas, coordinatorVideo);
                await stream.renderVideo(canvas, currentUser.userId, canvas.width, canvas.height, 0, 0, this.videoQuality);
                this.coordinatorVideoActive = true;
                this.usingCanvasForCoordinator = true;
              }
            }
          }
        } catch (error) {
          console.error('Video force binding failed:', error);
        }
      }, 1000);
      //update device lists
      this.micDevices = await this.zoomService.getMicList();
      this.selectedMic = this.micDevices[0];
      this.speakerDevices = await this.zoomService.getSpeakerList();
      this.selectedSpeaker = this.speakerDevices[0];
      
      // Load existing media for this session
      await this.loadSessionMedia();
      
      // Start periodic media refresh
      this.startMediaRefresh();
      
      if (!this.isInspectorConnected) {
        this.notify.showToast('warn', 'GoVideo', 'Waiting for the inspector to join the session');
      }

    } catch (error: any) {
      if (error.type !== 'INVALID_OPERATION' || error.reason !== 'Video is started') {
        console.error('Error starting coordinator video and audio', error);
      }
    } finally {
      this.isSessionStarting = false;
      this.cdr.reattach();
      this.cdr.detectChanges();
    }
  }

  record(event: any): void {
    try {
      if (!this.cloudRecording) {
      this.cloudRecording = this.zoomClient?.getRecordingClient();
      if (!this.cloudRecording) {
        throw new Error('Failed to initialize recording client');
      }
      }
      if (!this.isRecording) {
        console.log('🔴 Starting cloud recording...');
        console.log('🔍 cloudRecording object:', this.cloudRecording);
        const startResult = this.cloudRecording.startCloudRecording();
        console.log('✅ startCloudRecording() called, result:', startResult);
        this.notify.showToast('success', 'GoVideo', 'Recording started');
        this.isRecording = true;
        this.recordingStartTime = new Date().toISOString();
        console.log('✅ Recording started. State:', {
          isRecording: this.isRecording,
          sequence: this.recordingSequence,
          startTime: this.recordingStartTime
        });
        
        // Verify state persists after a moment
        setTimeout(() => {
          console.log('🔍 Recording state after 1 second:', {
            isRecording: this.isRecording
          });
        }, 1000);
      } else {
        console.log('⏹️ Stopping cloud recording...');
        this.cloudRecording.stopCloudRecording();
        this.notify.showToast('success', 'GoVideo', 'Recording stopped');
        this.isRecording = false;
        this.recordingSequence++; // Increment for next recording segment
        this.recordingStartTime = null;
        console.log('✅ Recording stopped. State:', {
          isRecording: this.isRecording,
          nextSequence: this.recordingSequence
        });
      }
    } catch (error) {
      console.error('Error starting cloud recording', error);
      this.notify.showToast('error', 'GoVideo', 'Unable to start/stop recording', 0);
      this.isRecording = false;
    }
  }

  async takePhoto(event: any): Promise<void> {
    // Prevent multiple captures
    if (this.isCapturingPhoto) {
      console.log('⚠️ Photo capture already in progress, skipping');
      return;
    }
    
    if (!this.zoomService.inspector) {
      this.notify.showToast('error', 'Error', 'Inspector not found');
      return;
    }
    
    try {
      console.log('📸 takePhoto() called - state:', {
        isRecording: this.isRecording,
        sequence: this.recordingSequence,
        isMobileDevice: this.isInspectorOnMobileDevice
      });

      // Stop recording before photo capture for mobile devices AND only if currently recording
      // Mobile devices need to stop the Zoom stream to capture high-res photos
      // Desktop devices can continue recording while capturing photos
      if (this.isInspectorOnMobileDevice && this.isRecording) {
        console.log('⏹️ Stopping recording for photo capture on mobile device (will auto-restart after)...');

        if (!this.cloudRecording) {
          console.error('❌ Cloud recording client not initialized');
          return;
        }

        try {
          await this.cloudRecording.stopCloudRecording();
          this.isRecording = false;
          this.recordingSequence++; // Increment sequence for next segment
          this.recordingStartTime = null;
          this.wasRecordingBeforePhotoCapture = true; // Set flag so we know to auto-restart
          console.log('✅ Recording stopped for photo. Next sequence will be:', this.recordingSequence);
        } catch (error) {
          console.error('❌ Error stopping recording:', error);
        }
      } else if (!this.isRecording) {
        console.log('ℹ️ No active recording - photo capture will proceed normally');
      } else if (!this.isInspectorOnMobileDevice) {
        console.log('ℹ️ Desktop device - recording continues during photo capture');
      }
      
      // Start capture state and visual effects
      this.isCapturingPhoto = true;
      this.cdr.detectChanges();
      
      const command: CommandEvent = {
        type: CommandEventType.IMAGE_CAPTURE_REQUESTED,
        data: {},
      }
      await this.zoomService.sendCommand(command, this.zoomService.inspector.userId || 0);
      this.notify.showToast('info', 'Photo Request', 'Photo capture request sent to inspector');

      // Safety timeout: reset guard after 30s in case completion event is never received
      this.photoCaptureTimeout = setTimeout(() => {
        if (this.isCapturingPhoto) {
          console.warn('⚠️ Photo capture guard reset by safety timeout (30s)');
          this.isCapturingPhoto = false;
          this.cdr.detectChanges();
        }
      }, 30000);

    } catch (error) {
      console.error('Error in takePhoto:', error);
      this.isCapturingPhoto = false;
      
      // Recording continues uninterrupted (no pause during photo capture)
      
      this.cdr.detectChanges();
      this.notify.showToast('error', 'Error', 'Failed to request photo capture');
    }
  }

  onPhotoCaptureRequested() {
    this.takePhoto(null);
  }

  onMediaAdded(media: MediaItem) {
    this.imageCount++;
    this.notify.showToast('success', 'Media Captured', `Media ${this.imageCount} captured successfully`);
  }

  onMediaDeleted(mediaId: string) {
    this.imageCount = Math.max(0, this.imageCount - 1);
  }

  endSession(event: any): void {
    this.confirmationService.confirm({
      message: 'This will also end the inspector\'s session. Are you sure you want to leave and end the session?',
      header: 'Leave Govideo Session?',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon:"none",
      rejectIcon:"none",
      // rejectButtonStyleClass:"p-button-text",
      closeOnEscape: true,
      blockScroll: true,
      dismissableMask: true,
      accept: async () => {
        try {
          this.cdr.detach();
          
          // Show loading message
          this.notify.showToast('info', 'GoVideo', 'Ending session and processing recordings...', 0);
          
          await this.sendSessionEndRequest();
          const user = this.zoomClient?.getCurrentUserInfo() || null;
          await this.zoomService.leave();
          this.zoomService.destroyClient();
          this.isSessionStarted = false;
          this.sessionEnded = true;
          this.cdr.reattach();
          
          // Clear the loading message and show completion
          this.messageService.clear();
          this.notify.showToast('success', 'GoVideo', 'Session ended successfully');
          this.router.navigate(['/session-end']);
        } catch (error) {
          this.cdr.reattach();
          this.messageService.clear();
          this.notify.showToast('error', 'GoVideo', 'Failed to end the session', 0);
          throw new Error('Failed to end session');
        }
      },
      reject: () => {
          this.notify.showToast('info', 'GoVideo', 'You are still connected to the session', 3000);
      }
    });
  }

  async startInspectorVideo(participant?: Participant) {
    try {
      if (!this.zoomClient) {
        throw new Error('Zoom client not initialized');
      }
      
      let inspector = participant || undefined; 
      if (!inspector) {
        const participants = this.zoomClient.getAllUser();
        inspector = participants.find((p) => p.userIdentity === 'inspector' || p.userIdentity === 'Inspector');
      }
      
      if (!inspector) {
        return;
      }
      
      // Check both isVideoConnect and bVideoOn - both must be true
      if (inspector && inspector.bVideoOn) {
        if (!inspector.isVideoConnect) {
          return;
        }
        const stream = this.zoomClient.getMediaStream();
        if (!stream) {
          throw new Error('Media stream not found');
        }
  
        const container = this.inspectorVideoContainer.nativeElement;
        if (!container) {
          throw new Error('Inspector video container not found');
        }

        // Clean up any existing video element
        if (this.inspectorVideoElement && this.inspectorVideoElement.parentNode) {
          this.inspectorVideoElement.parentNode.removeChild(this.inspectorVideoElement);
          this.inspectorVideoElement = null;
        }

        // Detach change detection before Zoom SDK manipulates DOM
        this.cdr.detach();
        
        // Use attachVideo() - per Zoom docs, this returns a video element we must append
        const userVideo = await stream.attachVideo(
          inspector.userId,
          3  // Quality: 3 = 720p (per Zoom SDK docs)
        );
        
        // Per Zoom docs: manually append the returned video element to the container
        if (userVideo && userVideo instanceof HTMLElement) {
          // Apply inline styles required for Zoom SDK custom elements (Shadow DOM doesn't inherit CSS)
          userVideo.style.width = '100%';
          userVideo.style.height = '100%';
          userVideo.style.minHeight = '500px';
          userVideo.style.display = 'flex';
          userVideo.style.flexDirection = 'column';
          userVideo.style.position = 'relative';
          userVideo.style.flex = '1 1 auto';
          (userVideo.style as any).webkitBoxFlex = '1';
          
          container.appendChild(userVideo);
          this.inspectorVideoElement = userVideo;
          this.isInspectorConnected = true;
          
          // Apply styles to inner div after Zoom SDK initializes it
          setTimeout(() => {
            if (userVideo.children.length > 0) {
              const innerDiv = userVideo.children[0] as HTMLElement;
              innerDiv.style.width = '100%';
              innerDiv.style.height = '100%';
              innerDiv.style.minHeight = '500px';
              innerDiv.style.display = 'flex';
              innerDiv.style.position = 'relative';
              innerDiv.style.flex = '1 1 auto';
              (innerDiv.style as any).webkitBoxFlex = '1';
            }
            
            // Reattach change detection after DOM manipulation is complete
            this.cdr.reattach();
            this.cdr.detectChanges();
          }, 100);
        } else {
          this.cdr.reattach();
          throw new Error('attachVideo did not return a valid HTMLElement');
        }
      }
    } catch (error: any) {
      console.error('Error in startInspectorVideo:', error);
      
      // Ensure CDR is reattached even on error
      try {
        this.cdr.reattach();
      } catch (e) {
        // CDR might already be attached
      }
      
      // Only set isInspectorConnected to false for actual connection/participant issues
      if (error.message && (
        error.message.includes('Inspector not found') || 
        error.message.includes('Zoom client not initialized') ||
        error.message.includes('user not found')
      )) {
        this.isInspectorConnected = false;
      }
    }
  }

  private resizeInspectorCanvas(): void {
    // With attachVideo(), the Zoom SDK handles responsive sizing automatically
    // No manual intervention needed
    try {
      if (!this.isInspectorConnected || !this.zoomClient) {
        return;
      }

      // Window resized - SDK handles video element sizing automatically
    } catch (error) {
      console.error('Error in resize handler:', error);
    }
  }





  async selectMic(event: any): Promise<void> {
    try {
      await this.zoomService.selectMic(event.value);
    } catch (error) {
      console.error('Error selecting audio device', error);
    }
  }

  async selectSpeaker(event: any): Promise<void> {
    try {
      await this.zoomService.selectMic(event.value);
    } catch (error) {
      console.error('Error selecting audio device', error);
    }
  }

  async getCameraList() {
    if (!this.zoomService.inspector || !this.zoomService.coordinator) {
      throw new Error('Inspector or coordinator not found');
    }
    const commandEvent: CommandEvent = {
      type: CommandEventType.CAMERA_SWITCH_REQUESTED,
      data: null,
    }   
    await this.zoomService.sendCommand(commandEvent, this.zoomService.inspector.userId);
  }

  async getInspectorCameraList(event: any) {
    if (!this.zoomService.inspector) {
      throw new Error('Get camera list: Inspector not found');
    }
    this.zoomService.inspectorCameraList = null;
    this.inspectorCameraList = null;
    const commandEvent: CommandEvent = {
      type: CommandEventType.CAMERA_GET_LIST_REQUESTED,
      data: null,
    }
    const commandResponse = await this.zoomService.sendCommand(commandEvent, this.zoomService.inspector.userId);
    const checkCameraListInterval = setInterval(async () => {
      if (!this.zoomService.inspectorCameraList) {
        return;
      }
      if (this.zoomService.inspectorCameraList.length > 0) {
        this.inspectorCameraList = this.zoomService.inspectorCameraList;
        this.selectedInspectorCamera = this.inspectorCameraList[0];
        clearInterval(checkCameraListInterval);
      }
    }, 1000);
  }

  async selectInspectorCamera(event: any) {
    if (!this.zoomService.inspector) {
      throw new Error('Select camera: Inspector not found');
    }
    const commandEvent: CommandEvent = {
      type: CommandEventType.CAMERA_SWITCH_REQUESTED,
      data: { cameraId: event.value },
    }
    await this.zoomService.sendCommand(commandEvent, this.zoomService.inspector.userId);
  
    this.inspectorCameraList = null;
  
  }

  async sendSessionEndRequest(): Promise<void> {
    const inspectorId = this.zoomService.inspector?.userId || 0;
    if (inspectorId) {
      await this.zoomService.sendCommand({
        type: CommandEventType.SESSION_END_REQUESTED,
        data: {},
      }, inspectorId);
    }
    return;
  }

  // UI Getter Methods
  get shouldShowPhotoButton(): boolean {
    // Show button if:
    // 1. We're currently capturing a photo, OR
    // 2. Inspector is connected, OR  
    // 3. Inspector exists in zoom service (more reliable check)
    return this.isCapturingPhoto || this.isInspectorConnected || !!this.zoomService.inspector;
  }

  // UI Toggle Methods
  async toggleCoordinatorVideo(): Promise<void> {
    if (!this.zoomClient || !this.isSessionStarted) {
      return;
    }
    
    try {
      const stream = this.zoomClient.getMediaStream();
      if (!stream) {
        return;
      }
      
      if (this.showCoordinatorVideo && this.coordinatorVideoActive) {
        // Currently showing video, so stop it
        await stream.stopVideo();
        this.showCoordinatorVideo = false;
        this.coordinatorVideoActive = false;
        this.usingCanvasForCoordinator = false;
      } else {
        // Currently hidden, so start video
        this.showCoordinatorVideo = true;
        this.coordinatorVideoActive = true;
        
        // Use a simpler approach: just restart the video and let the force binding handle the display
        const coordinatorVideo = this.coordinatorVideo?.nativeElement;
        if (coordinatorVideo) {
          try {
            await stream.startVideo({
              videoElement: coordinatorVideo,
            });
            this.usingCanvasForCoordinator = false;
          } catch (error) {
            // If video element fails, start without element and trigger force binding
            await stream.startVideo();
            
            // Trigger the same force binding logic as initial startup
            setTimeout(async () => {
              try {
                const currentUser = this.zoomClient?.getCurrentUserInfo();
                if (currentUser && coordinatorVideo && coordinatorVideo.srcObject === null) {
                  // Try to restart with explicit element binding
                  try {
                    await stream.stopVideo();
                    await new Promise(resolve => setTimeout(resolve, 100));
                    await stream.startVideo({
                      videoElement: coordinatorVideo,
                    });
                  } catch (retryError) {
                    // Final fallback to canvas
                    if (coordinatorVideo.srcObject === null) {
                      const canvas = document.createElement('canvas');
                      canvas.width = 200;
                      canvas.height = 150;
                      canvas.className = 'coordinator-video';
                      
                      const parent = coordinatorVideo.parentElement;
                      if (parent) {
                        parent.replaceChild(canvas, coordinatorVideo);
                        await stream.renderVideo(canvas, currentUser.userId, canvas.width, canvas.height, 0, 0, this.videoQuality);
                        this.usingCanvasForCoordinator = true;
                      }
                    }
                  }
                }
              } catch (forceBindError) {
                console.error('Force binding failed on toggle:', forceBindError);
              }
            }, 1000); // Same delay as initial startup
          }
        }
      }
      
      // Force UI update
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling coordinator video:', error);
    }
  }

  toggleThumbnailDrawer(): void {
    this.showThumbnailDrawer = !this.showThumbnailDrawer;
    this.cdr.detectChanges();
  }

  closeThumbnailDrawer(): void {
    // Don't close if pinned
    if (!this.isThumbnailDrawerPinned) {
      this.showThumbnailDrawer = false;
      this.cdr.detectChanges();
    }
  }

  onThumbnailDrawerPinToggled(isPinned: boolean): void {
    this.isThumbnailDrawerPinned = isPinned;
    this.cdr.detectChanges();
  }

  onDrawerMouseLeave(): void {
    // Auto-close drawer when mouse leaves, unless it's pinned
    if (!this.isThumbnailDrawerPinned) {
      this.showThumbnailDrawer = false;
      this.cdr.detectChanges();
    }
  }

  openMediaGalleryDialog(): void {
    this.showMediaGalleryDialog = true;
    this.cdr.detectChanges();
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    // Allow layout to update, then resize canvas
    setTimeout(() => {
      this.resizeInspectorCanvas();
    }, 350); // Slightly longer than CSS transition (0.3s)
  }

  toggleDeviceSettings(): void {
    this.showDeviceSettings = !this.showDeviceSettings;
    if (this.showDeviceSettings) {
      // Load device lists when showing settings
      this.loadDeviceLists();
    }
  }

  async loadDeviceLists(): Promise<void> {
    try {
      this.micDevices = await this.zoomService.getMicList();
      this.speakerDevices = await this.zoomService.getSpeakerList();
    } catch (error) {
      console.error('Error loading device lists:', error);
    }
  }

  // Helper method to check if media item is a photo/image
  private isImage(item: MediaItem): boolean {
    if (!item) return false;
    return item.type?.startsWith('image/') || (!item.type?.startsWith('video/') && item.type !== undefined);
  }

  async loadSessionMedia(): Promise<void> {
    try {
      await this.mediaService.getSessionMedia(this.sessionId);
    } catch (error) {
      console.error('Error loading session media:', error);
    }
  }

  // Periodically refresh media to get new photos captured by inspector
  private mediaRefreshInterval: any;
  
  startMediaRefresh(): void {
    // Refresh media every 5 seconds during active session
    this.mediaRefreshInterval = setInterval(() => {
      if (this.isSessionStarted) {
        this.loadSessionMedia();
      }
    }, 5000);
  }

  stopMediaRefresh(): void {
    if (this.mediaRefreshInterval) {
      clearInterval(this.mediaRefreshInterval);
      this.mediaRefreshInterval = null;
    }
  }

  // Guest Link Dialog Methods
  async openGuestLinkDialog() {
    try {
      // Use the create endpoint which returns existing session or creates new one with full URLs including tokens
      const session = await firstValueFrom(
        this.sessionManagerService.createSession({ sessionId: this.sessionId })
      );

      if (session && session.guestConnectUrl) {
        this.guestLinkSession = {
          sessionId: session.sessionId,
          guestUserIdentity: 'inspector',
          guestConnectUrl: session.guestConnectUrl
        };

        this.showGuestLinkDialog = true;
      } else {
        console.error('No guestConnectUrl available');
        this.notify.showToast('error', 'Error', 'Could not generate guest link');
      }
    } catch (error: any) {
      console.error('Failed to open guest link dialog:', error);
      this.notify.showToast('error', 'Error', 'Failed to open guest link dialog');
    }
  }

  // Location Dialog Methods
  openLocationDialog() {
    if (this.locationTrackingActive && this.sessionId) {
      this.showLocationDialog = true;
    }
  }
}
